import { createLink, type LinkComponent } from '@tanstack/react-router'
import { forwardRef, useEffect, useState, type AnchorHTMLAttributes, type MouseEvent } from 'react'
import { cn } from '@/lib/utils'
import { SWITCH_HAPTIC_STYLE, switchHapticWanted, tapFeedback } from '../core/haptics'

// THE link for anything that drives a route transition, so a transition's tap behaviour — the haptic, and the
// slide that starts on this very frame — is defined once. A real <a> through createLink: middle-clickable,
// shareable, SSR-stable, and still injecting aria-current. Styling is entirely the caller's.
// The iOS haptic overlay is RENDERED from an effect-set flag. Appended with host.appendChild instead, it was a
// node React has no fiber for inside a React-managed <a>, and a tap on it made React throw during the concurrent
// render and recover by synchronously re-rendering the whole root — on every nav on iOS (recoverable error #520).
// Guarded by route-transition-haptics.e2e.test.ts.

const TransitionLinkBase = forwardRef<HTMLAnchorElement, AnchorHTMLAttributes<HTMLAnchorElement>>(
  ({ onClick, className, children, ...props }, forwardedRef) => {
    // In an effect, not render: it reads media queries and the Vibration API, so SSR and hydration must be false.
    const [wantsSwitch, setWantsSwitch] = useState(false)
    // oxlint-disable-next-line react/set-state-in-effect -- hydration parity: reads media queries + the Vibration API, so it must be false on the server and on the hydrating first client render (see the comment above)
    useEffect(() => setWantsSwitch(switchHapticWanted()), [])
    return (
      <a
        ref={forwardedRef}
        // `relative` unconditionally, so the class list is identical on both sides and the overlay's inset resolves
        // against the link. A caller's own positioning still wins, since `cn` is tailwind-merge.
        className={cn('relative', className)}
        // Before delegating, so the tick lands with the start of the motion rather than after it.
        // Not for a tap that goes nowhere: a link pointing at the pathname we are already on does not slide, so a buzz
        // would be feedback for nothing. Compared off the resolved href, not aria-current, whose default includeSearch
        // misses exactly that same-path/different-search case.
        onClick={(event: MouseEvent<HTMLAnchorElement>) => {
          if (event.currentTarget.pathname !== window.location.pathname) tapFeedback()
          onClick?.(event)
        }}
        {...props}
      >
        {children}
        {wantsSwitch ? (
          <input
            type="checkbox"
            // The `switch` ATTRIBUTE is the whole feature and is not in React's prop types; React 19 passes it through.
            ref={(el) => el?.setAttribute('switch', '')}
            // Invisible to the eye and to assistive tech: the <a> stays the accessible control, and this is not a tab stop.
            tabIndex={-1}
            aria-hidden="true"
            style={SWITCH_HAPTIC_STYLE}
          />
        ) : null}
      </a>
    )
  },
)
TransitionLinkBase.displayName = 'TransitionLinkBase'

const CreatedTransitionLink = createLink(TransitionLinkBase)

export const TransitionLink: LinkComponent<typeof TransitionLinkBase> = (props) => <CreatedTransitionLink {...props} />
